import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, API_BASE } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookmarks, setBookmarks] = useState(new Set()); // "type:id" strings

  const loadBookmarks = useCallback(async () => {
    try {
      const r = await api.get("/bookmarks");
      setBookmarks(new Set(r.data.map((b) => `${b.target_type}:${b.target_id}`)));
    } catch {
      setBookmarks(new Set());
    }
  }, []);

  const refresh = async () => {
    const token = localStorage.getItem("tb_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      loadBookmarks();
    } catch (e) {
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = (token, userData) => {
    setToken(token);
    setUser(userData);
    loadBookmarks();
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setBookmarks(new Set());
  };

  const toggleBookmark = async (type, id) => {
    const key = `${type}:${id}`;
    const wasSaved = bookmarks.has(key);
    // Optimistic update
    setBookmarks((prev) => {
      const next = new Set(prev);
      if (wasSaved) next.delete(key);
      else next.add(key);
      return next;
    });
    try {
      await api.post(`/bookmarks/${type}/${id}`);
    } catch {
      // Revert
      setBookmarks((prev) => {
        const next = new Set(prev);
        if (wasSaved) next.add(key);
        else next.delete(key);
        return next;
      });
    }
  };

  const isBookmarked = (type, id) => bookmarks.has(`${type}:${id}`);

  return (
    <AuthContext.Provider
      value={{ user, loading, login, logout, refresh, bookmarks, isBookmarked, toggleBookmark }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);


/** Global WebSocket hook — pushes real-time notifications into a callback. */
export function useNotificationsSocket(onNotification) {
  useEffect(() => {
    const token = localStorage.getItem("tb_token");
    if (!token) return;
    // Convert https://foo → wss://foo, http://foo → ws://foo
    const wsUrl = `${API_BASE.replace(/^http/, "ws")}/ws/notifications?token=${token}`;
    let ws;
    let reconnectTimer;
    let stopped = false;
    const connect = () => {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.kind !== "hello") onNotification?.(data);
        } catch (_) {}
      };
      ws.onclose = () => {
        if (!stopped) reconnectTimer = setTimeout(connect, 3000);
      };
      ws.onerror = () => { try { ws.close(); } catch (_) {} };
    };
    connect();
    return () => {
      stopped = true;
      clearTimeout(reconnectTimer);
      try { ws?.close(); } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
